// Global Instructions Rule Applied!
//
// Security full-form payload (view__security_detail): the security's identity
// + key commercial terms (raw ids alongside labels so the form's selects can
// edit them), plus every tranche holding the security with its current
// per-fund position. Strict like the other detail models — a new RPC key must
// be added here or the fetch fails loudly.
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

export const SecurityDetailSchema = z
  .object({
    securities__id: z.string(),
    securities__name: z.string().nullish(),
    accounts__id: z.string().nullish(),
    accounts__name: z.string().nullish(),
    cfg_security_types__id: z.number().nullish(),
    cfg_security_types__label: z.string().nullish(),
    cfg_asset_valuation_types__id: z.number().nullish(),
    cfg_asset_valuation_types__label: z.string().nullish(),
    currencies__id: z.string().nullish(),
    currencies__code: z.string().nullish(),
    currencies__symbol: z.string().nullish(),
    securities__issue_price: z.number().nullish(),
    securities__nominal_value: z.number().nullish(),
    securities__interest_rate: z.number().nullish(),
    securities__maturity_date: z.string().nullish(),
    securities__maturity_months: z.number().nullish(),
    securities__valuation_cap: z.number().nullish(),
    securities__valuation_floor: z.number().nullish(),
    securities__conversion_discount: z.number().nullish(),
    securities__strike_price: z.number().nullish(),
    securities__fd_common_equivalent: z.number().nullish(),
    cfg_valuation_cap_types__id: z.number().nullish(),
    cfg_interest_rate_types__id: z.number().nullish(),
    cfg_compounding_frequencies__id: z.number().nullish(),
    cfg_day_count_conventions__id: z.number().nullish(),
    // interest terms (docs/INTEREST_ACCRUAL_DESIGN.md section 3.3)
    securities__first_period_end_date: z.string().nullish(),
    securities__accrues_after_maturity: z.boolean().nullish(),
    securities__interest_accrues_from: z.string().nullish(),
    cfg_interest_payment_types__id: z.number().nullish(),
    securities__notes: z.string().nullish()
  })
  .strict()

export type SecurityDetail = z.infer<typeof SecurityDetailSchema>

// security_interest_rate_periods: the rates in force from a date (a default
// rate after maturity, a stepped coupon), overriding the security's own rate
// from effective_from.
export const SecurityRatePeriodSchema = z
  .object({
    security_interest_rate_periods__id: z.string(),
    security_interest_rate_periods__effective_from: z.string(),
    security_interest_rate_periods__interest_rate: z.number(),
    cfg_day_count_conventions__id: z.number().nullish(),
    cfg_day_count_conventions__label: z.string().nullish()
  })
  .strict()

export type SecurityRatePeriod = z.infer<typeof SecurityRatePeriodSchema>

export const SecurityTrancheEntrySchema = z
  .object({
    tranches__id: z.string(),
    tranches__name: z.string().nullish(),
    funds__id: z.string().nullish(),
    funds__name: z.string().nullish(),
    currencies__symbol: z.string().nullish(),
    prog__date_invested: z.string().nullish(),
    prog__date_exited: z.string().nullish(),
    prog__no_held: z.number().nullish(),
    prog__cost_held: z.number().nullish(),
    prog__fmv: z.number().nullish(),
    prog__unit_price: z.number().nullish(),
    prog__perc_exited: z.number().nullish()
  })
  .strict()

export type SecurityTrancheEntry = z.infer<typeof SecurityTrancheEntrySchema>

export const SecurityDetailPayloadSchema = z
  .object({
    security: SecurityDetailSchema,
    tranches: z.array(SecurityTrancheEntrySchema),
    rate_periods: z.array(SecurityRatePeriodSchema).optional().default([])
  })
  .strict()

export type SecurityDetailPayload = z.infer<typeof SecurityDetailPayloadSchema>

export function validateSecurityDetailPayload(data: unknown): SecurityDetailPayload {
  try {
    return validateWithZodSchema(
      SecurityDetailPayloadSchema,
      data,
      'security detail'
    ) as SecurityDetailPayload
  } catch (e) {
    throw new Error(`Security detail validation failed: ${e}`)
  }
}
